import type { BusinessRiskArea, OpsRiskDimension, OpsRiskSeverity } from '@nadoutong/contracts';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useT, type I18nKey } from '../i18n/index.ts';
import { Chip, buttonClass } from '../ui/index.ts';
import type { ChipTone } from '../ui/index.ts';
import { formatLocalTimestamp } from './formatters.ts';

/**
 * Shared rendering for unified risk items (Epic #853 / #858).
 *
 * One severity vocabulary (`critical | warning | info`) and one
 * dimension label set (`supply | finance | traffic`) across every
 * surface that renders risks — `/business/risk`, `/business/overview`,
 * and the `/business/traffic` risk queue all render through this
 * module, so no page carries its own ad-hoc severity mapping anymore.
 *
 * Window semantics differ per dimension (epic #853 design decision) and
 * are surfaced next to the dimension label:
 *
 *   - supply  — trailing-24h dashboard snapshot.
 *   - finance — unresolved backlog, no time window.
 *   - traffic — the traffic overview's own current window; pages that
 *     control that window (the traffic monitor) pass an explicit
 *     `windowLabel` naming it.
 */

export function riskSeverityChipTone(severity: OpsRiskSeverity): ChipTone {
  if (severity === 'critical') return 'bad';
  if (severity === 'warning') return 'warn';
  return 'neutral';
}

export function RiskSeverityPill({ severity }: { readonly severity: OpsRiskSeverity }): JSX.Element {
  return <Chip tone={riskSeverityChipTone(severity)}>{severity}</Chip>;
}

const DIMENSION_LABEL_KEYS: Record<OpsRiskDimension, I18nKey> = {
  supply: 'risk.dimension.supply',
  finance: 'risk.dimension.finance',
  traffic: 'risk.dimension.traffic',
};

const DIMENSION_WINDOW_KEYS: Record<OpsRiskDimension, I18nKey> = {
  supply: 'risk.window.supply',
  finance: 'risk.window.finance',
  traffic: 'risk.window.traffic',
};

/**
 * Narrow a legacy `BusinessRiskArea` onto the unified dimension set.
 * The unified aggregation only ever emits `supply | finance | traffic`;
 * `customers` / `system` remain in the wire union for back-compat and
 * fall back to rendering the raw area text.
 */
export function riskDimensionFromArea(area: BusinessRiskArea): OpsRiskDimension | null {
  return area === 'supply' || area === 'finance' || area === 'traffic' ? area : null;
}

export function RiskDimensionTag({
  dimension,
  windowLabel,
}: {
  readonly dimension: OpsRiskDimension;
  /** Overrides the default per-dimension window hint (traffic monitor
   * passes its own selected window). */
  readonly windowLabel?: string;
}): JSX.Element {
  const t = useT();
  return (
    <span className="text-label text-paper-low">
      {t(DIMENSION_LABEL_KEYS[dimension])} · {windowLabel ?? t(DIMENSION_WINDOW_KEYS[dimension])}
    </span>
  );
}

export interface RiskCardItem {
  readonly id: string;
  /** `null` = legacy area outside the unified dimension set; the raw
   * `dimensionFallback` text is rendered instead of a dimension tag. */
  readonly dimension: OpsRiskDimension | null;
  readonly dimensionFallback?: string;
  readonly severity: OpsRiskSeverity;
  readonly title: string;
  readonly detail: string;
  readonly targetLabel: string;
  readonly href: string;
  readonly occurredAt: string | null;
}

export function RiskItemCard({
  item,
  windowLabel,
  testId,
}: {
  readonly item: RiskCardItem;
  readonly windowLabel?: string;
  readonly testId?: string;
}): JSX.Element {
  return (
    <div className="rounded-lg bg-well px-4 py-3" data-testid={testId}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <RiskSeverityPill severity={item.severity} />
            {item.dimension !== null ? (
              <RiskDimensionTag dimension={item.dimension} windowLabel={windowLabel} />
            ) : (
              <span className="text-label text-paper-low">{item.dimensionFallback ?? ''}</span>
            )}
          </div>
          <p className="m-0 mt-2 text-body font-medium text-paper">{item.title}</p>
          <p className="m-0 mt-1 break-words text-dense text-paper-low">{item.detail}</p>
        </div>
        <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
          <Link to={item.href} className={buttonClass('secondary', 'sm')}>
            <span>{item.targetLabel}</span>
            <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
          </Link>
          {item.occurredAt !== null ? (
            <span className="font-mono text-micro text-paper-low">
              {formatLocalTimestamp(item.occurredAt)}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
